<template>
  <div class="ut" :class="{ 'ut-normal': normal }">
    <div class="ut-intro">
      <div class="ut-icon"><i class="fa" :class="normal ? 'fa-life-ring' : 'fa-bell'"></i></div>
      <div v-if="normal">
        <div class="ut-title">تذكرة دعم فني</div>
        <div class="ut-sub">تصل تذكرتك لفريق الدعم الفني ويرد عليك داخل التذكرة في أقرب وقت. للأسئلة العامة اسأل المساعد الذكي.</div>
      </div>
      <div v-else>
        <div class="ut-title">الدعم الفني السريع <span class="ut-badge"><i class="fa fa-bolt"></i> عاجل</span></div>
        <div class="ut-sub">تصل تذكرتك فوراً لجوالات فريق الدعم. استخدمها للمواقف العاجلة التي تحتاج تدخلاً الآن، وللأسئلة العامة اسأل المساعد الذكي.</div>
      </div>
    </div>

    <label class="ut-label" for="ut-desc">وصف المشكلة <span class="ut-req">*</span></label>
    <textarea id="ut-desc" ref="desc" v-model="title" class="ut-text" rows="4" maxlength="2000"
              placeholder="اكتب ما حدث: ماذا كنت تفعل، وما الرسالة التي ظهرت، ومتى بدأت المشكلة…"
              :class="{ 'ut-invalid': titleError }" @input="titleError = ''"></textarea>
    <div v-if="titleError" class="ut-field-error">{{ titleError }}</div>

    <div class="ut-label">الخدمة (اختياري)</div>
    <div class="ut-chips" role="radiogroup" aria-label="الخدمة">
      <button v-for="s in services" :key="s" type="button" class="ut-chip" :class="{ on: service == s }"
              role="radio" :aria-checked="service == s ? 'true' : 'false'" @click="pickService(s)">{{ s }}</button>
    </div>

    <div class="ut-label">المرفقات (اختياري)</div>
    <div class="ut-sub ut-sub-tight">صورة للشاشة أو ملف، أو اشرح المشكلة برسالة صوتية.</div>
    <div class="ut-attach-row">
      <button type="button" class="ut-attach-btn" :disabled="files.length >= maxFiles" @click="pickFile">
        <i class="fa fa-paperclip"></i> صورة أو ملف
      </button>
      <voice-recorder v-if="!voice" @recorded="addVoice" />
      <input ref="file" type="file" class="ut-hidden" multiple :accept="accept" @change="filesPicked">
    </div>
    <div v-if="limitNote" class="ut-field-error">{{ limitNote }}</div>

    <div v-if="items.length" class="ut-items">
      <div v-for="a in items" :key="a.id" class="ut-item" :class="'ut-item-' + a.status">
        <i class="fa ut-item-icon" :class="a.kind == 'image' ? 'fa-picture-o' : a.kind == 'voice' ? 'fa-microphone' : 'fa-file-o'"></i>
        <div class="ut-item-main">
          <div class="ut-item-name">{{ a.label }}</div>
          <audio v-if="a.kind == 'voice' && a.preview" :src="a.preview" controls preload="metadata" class="ut-audio"></audio>
          <div class="ut-item-state">
            <template v-if="a.status == 'uploading'">جارٍ الرفع… {{ a.progress }}%</template>
            <template v-else-if="a.status == 'failed'">{{ a.error }}</template>
            <template v-else>جاهز</template>
          </div>
        </div>
        <button v-if="a.status == 'failed'" type="button" class="ut-item-btn" @click="retry(a)">
          <i class="fa fa-repeat"></i> إعادة الرفع
        </button>
        <button type="button" class="ut-item-btn ut-item-del" title="حذف" aria-label="حذف" @click="remove(a)">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="ut-error">{{ error }}</div>

    <button type="button" class="ut-submit" :disabled="sending" @click="submit">
      <i class="fa" :class="sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
      {{ sending ? 'جارٍ الإرسال…' : (normal ? 'إرسال التذكرة' : 'إرسال للدعم الفني السريع') }}
    </button>
  </div>
</template>

<script>
import { call, upload, kindOf, SERVICES, MAX_FILES, FILE_ACCEPT, seconds } from './support-api';
import VoiceRecorder from './VoiceRecorder.vue';

var seq = 0;

// «الدعم الفني السريع»: an urgent ticket with attachments, same sequence as the school app —
// every file uploads as soon as it's picked, /urgent carries the first one, the rest follow as
// messages of the ticket. Emits 'created' (ticket id). `normal`: «تذكرة دعم فني» (/ticket/create),
// for schools the support team stopped from urgent tickets. Emits 'paid-only' (message, state) when
// the server refuses: 'not_subscribed' (subscribe first) or 'blocked' (→ a normal ticket).
export default {
  components: { VoiceRecorder },
  props: {
    prefill: { type: String, default: '' },
    normal: { type: Boolean, default: false },
  },
  data(){
    return {
      title: this.prefill || '', service: '', files: [], voice: null, sending: false,
      error: '', titleError: '', limitNote: '',
      services: SERVICES, maxFiles: MAX_FILES, accept: FILE_ACCEPT,
    };
  },
  computed: {
    items(){ return this.voice ? this.files.concat([this.voice]) : this.files; },
  },
  watch: {
    prefill(v){ if (v && !this.title) this.title = v; },
  },
  beforeDestroy(){
    if (this.voice && this.voice.preview) URL.revokeObjectURL(this.voice.preview);
  },
  methods: {
    pickService(s){ this.service = this.service == s ? '' : s; },
    pickFile(){ this.$refs.file.click(); },
    filesPicked(e){
      var list = Array.prototype.slice.call(e.target.files || []);
      e.target.value = '';
      this.limitNote = '';
      var room = this.maxFiles - this.files.length;
      if (list.length > room) this.limitNote = 'يمكن إرفاق ' + this.maxFiles + ' ملفات كحد أقصى';
      var g = this;
      list.slice(0, Math.max(0, room)).forEach(function(f){
        var a = { id: ++seq, file: f, name: f.name, label: f.name, kind: kindOf(f.name), status: 'uploading', progress: 0, error: '', url: '', file_name: '', duration: 0 };
        g.files.push(a);
        g.send(a);
      });
    },
    addVoice(v){
      var a = { id: ++seq, file: v.blob, name: v.name, label: 'رسالة صوتية · ' + seconds(v.duration), kind: 'voice', status: 'uploading', progress: 0, error: '', url: '', file_name: '', duration: v.duration, preview: URL.createObjectURL(v.blob) };
      this.voice = a;
      this.send(a);
    },
    send(a){
      a.status = 'uploading';
      a.progress = 0;
      upload(a.file, a.name, function(p){ a.progress = p; }).then(function(r){
        if (r.ok) {
          a.status = 'done';
          a.url = r.data.url;
          a.kind = r.data.kind || a.kind;
          a.file_name = r.data.file_name || a.name;
        } else {
          a.status = 'failed';
          a.error = 'لم يُرفع — ' + r.message;
        }
      });
    },
    retry(a){ this.send(a); },
    remove(a){
      if (a === this.voice) {
        if (a.preview) URL.revokeObjectURL(a.preview);
        this.voice = null;
      } else {
        this.files = this.files.filter(function(x){ return x !== a; });
      }
      this.limitNote = '';
    },
    submit(){
      var g = this;
      g.error = '';
      var title = g.title.trim();
      if (!title) {
        g.titleError = 'اكتب وصف المشكلة أولاً';
        g.$refs.desc.focus();
        return;
      }
      if (g.items.some(function(a){ return a.status == 'uploading'; })) { g.error = 'انتظر حتى ينتهي رفع المرفقات'; return; }
      if (g.items.some(function(a){ return a.status == 'failed'; })) { g.error = 'بعض المرفقات لم تُرفع. أعد المحاولة أو احذفها'; return; }
      var ready = g.items.filter(function(a){ return a.status == 'done'; });
      var first = ready[0];
      g.sending = true;
      var body = { title: title, service: g.service };
      if (first) Object.assign(body, { file: first.url, kind: first.kind, file_name: first.file_name, duration: first.duration || 0 });
      call(g.normal ? '/ticket/create' : '/urgent', body).then(async function(r){
        if (!r.ok) {
          g.sending = false;
          // the page shows the matching notice instead of the form
          if (r.urgent_blocked) return g.$emit('paid-only', r.message, 'blocked');
          if (r.paid_only) return g.$emit('paid-only', r.message, 'not_subscribed');
          g.error = r.message || 'تعذّر إرسال التذكرة، حاول مرة أخرى';
          return;
        }
        var id = r.data && (r.data._id || r.data);
        for (var i = 1; i < ready.length; i++) {
          var a = ready[i];
          await call('/ticket/send', { id: id, message: '', file: a.url, kind: a.kind, file_name: a.file_name, duration: a.duration || 0 });
        }
        g.sending = false;
        g.$emit('created', id);
      });
    },
  },
};
</script>

<style scoped>
.ut { display: flex; flex-direction: column; gap: 8px; }
.ut-normal .ut-intro { background: rgba(100, 116, 139, 0.08); }
.ut-normal .ut-icon { background: linear-gradient(135deg, #64748b, #334155); }
.ut-normal .ut-title { color: #334155; }
.ut-normal .ut-submit { background: #334155; border-color: #334155; }
.ut-intro { display: flex; gap: 12px; align-items: flex-start; padding: 12px; border-radius: 14px; background: rgba(234, 84, 85, 0.07); margin-bottom: 6px; }
.ut-icon { width: 44px; height: 44px; flex: 0 0 auto; border-radius: 13px; background: linear-gradient(135deg, #f87171, #dc2626); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 19px; }
.ut-title { font-weight: 700; font-size: 16px; color: #c62828; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ut-badge { font-size: 12px; font-weight: 700; padding: 2px 10px; border-radius: 999px; background: rgba(234, 84, 85, 0.14); }
.ut-sub { font-size: 13px; opacity: 0.8; line-height: 1.8; }
.ut-sub-tight { margin-top: -4px; }
.ut-label { font-weight: 700; margin-top: 8px; }
.ut-req { color: #ea5455; }
.ut-text {
  width: 100%; resize: vertical; border-radius: 12px; padding: 10px 12px; line-height: 1.8; font: inherit; color: inherit;
  border: 1px solid rgba(127, 127, 127, 0.3); background: transparent;
}
.ut-text:focus { outline: 2px solid rgba(234, 84, 85, 0.3); border-color: #ea5455; }
.ut-invalid { border-color: #ea5455; }
.ut-field-error { color: #ea5455; font-size: 13px; }
.ut-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.ut-chip { border: 1px solid rgba(127, 127, 127, 0.3); background: transparent; color: inherit; border-radius: 999px; padding: 6px 14px; cursor: pointer; }
.ut-chip.on { background: #ea5455; border-color: #ea5455; color: #fff; }
.ut-attach-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-start; }
.ut-attach-btn {
  display: inline-flex; align-items: center; gap: 8px; height: 42px; padding: 0 14px; border-radius: 12px;
  border: 1px solid rgba(127, 127, 127, 0.28); background: transparent; color: inherit; font-weight: 600; cursor: pointer;
}
.ut-attach-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ut-hidden { display: none; }
.ut-items { display: flex; flex-direction: column; gap: 8px; }
.ut-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 12px; border: 1px solid rgba(127, 127, 127, 0.2); }
.ut-item-failed { border-color: rgba(234, 84, 85, 0.5); background: rgba(234, 84, 85, 0.05); }
.ut-item-icon { font-size: 18px; opacity: 0.7; width: 22px; text-align: center; }
.ut-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.ut-item-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ut-item-state { font-size: 12px; opacity: 0.75; }
.ut-item-failed .ut-item-state { color: #ea5455; opacity: 1; }
.ut-audio { width: 100%; max-width: 320px; height: 34px; }
.ut-item-btn { border: 0; background: transparent; color: var(--primary); font-weight: 600; cursor: pointer; padding: 6px; border-radius: 8px; white-space: nowrap; }
.ut-item-del { color: inherit; opacity: 0.6; }
.ut-error { padding: 10px 12px; border-radius: 10px; background: rgba(234, 84, 85, 0.1); color: #c62828; }
.ut-submit {
  margin-top: 10px; height: 48px; border: 0; border-radius: 12px; background: #ea5455; color: #fff; font-weight: 700; font-size: 15px;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 10px;
}
.ut-submit:disabled { opacity: 0.7; cursor: wait; }
</style>
